import React, { useState, useEffect, useRef } from 'react';

import { toast } from 'react-toastify';
import OTPInput from 'react-otp-input';
import { useHistory } from 'react-router-dom';
import { bool, shape, string } from 'prop-types';
import { useDispatch } from 'react-redux';

import Theme from '../../theme/Theme';
import { mfaSetup, mfaVerify } from '../../api';
import { Button, ErrorMsg, PageLoader } from '../../common';
import { MFAAuthenticationLogo2 } from '../../theme/images';
import { handleLoginNavigation, login } from '../../store/actions/userState';

export default function MFAFlow({
  isAlreadyScanned = false,
  userData,
  loginResponse,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const [step, setStep] = useState(isAlreadyScanned ? 3 : 1);
  const [otp, setOtp] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const customerObj = localStorage.getItem('customer');
  const [qrData, setQrData] = useState(null);
  const [isLoading, setIsLoading] = useState({
    loader: false,
    type: 'continue',
  });
  const [canSkip, setCanSkip] = useState(false);
  const timerRef = useRef(null);
  const SKIP_DELAY_SECONDS = 60;
  const [remainingSeconds, setRemainingSeconds] = useState(SKIP_DELAY_SECONDS);

  // ── Countdown timer ───────────────────────────────────────────
  useEffect(() => {
    if (isAlreadyScanned) {
      return undefined; // Explicitly return undefined to satisfy the lint rule
    }

    const isMFATimerExist = localStorage.getItem('mfaTimerStart');
    const now = Date.now();

    // If no timer stored yet, start fresh
    if (!isMFATimerExist) {
      localStorage.setItem('mfaTimerStart', now.toString());
    }

    const startTime = parseInt(isMFATimerExist || now.toString(), 10);

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(
        0,
        SKIP_DELAY_SECONDS - Math.floor(elapsed / 1000),
      );
      setRemainingSeconds(remaining);

      if (remaining === 0) {
        setCanSkip(true);
        clearInterval(timerRef.current);
        localStorage.removeItem('mfaTimerStart');
      }
    };

    tick(); // run immediately so UI shows correct value on mount
    timerRef.current = setInterval(tick, 1000);

    return () => clearInterval(timerRef.current);
  }, [isAlreadyScanned]);

  // ── Format seconds as MM:SS ───────────────────────────────────
  const formatTime = (secs) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleNext = () => setStep((prev) => prev + 1);
  const handleBack = () => setStep((prev) => prev - 1);

  const handleOtpChange = (code) => {
    setOtp(code);
    if (errorMsg) setErrorMsg('');
  };

  const handleContinue = async () => {
    setIsLoading({ loader: true, type: 'continue' });
    try {
      const data = await mfaSetup();
      setQrData(data?.qr_code);
      handleNext();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong.');
    } finally {
      setIsLoading({ loader: false, type: 'continue' });
    }
  };

  const handleVerify = async () => {
    if (otp.length < 6) return;
    setErrorMsg('');

    setIsLoading({ loader: true, type: 'verify' });

    if (!isAlreadyScanned) {
      mfaVerify({ otp })
        .then(() => {
          handleLoginNavigation(loginResponse, history, customerObj, '');
        })
        .catch((error) => {
          setOtp('');
          setErrorMsg(
            error?.response?.data?.message || 'Invalid code. Please try again.',
          );
        })
        .finally(() => {
          setIsLoading({ loader: false, type: 'verify' });
        });
    } else {
      const detail = {
        email: userData.email,
        password: userData.password,
        otp,
      };
      dispatch(login(detail, { customer: userData.customer }))
        .then((loginData) => {
          if (loginData?.status === 200 || loginData?.status === 201) {
            handleLoginNavigation(
              loginData?.data,
              history,
              userData.customer,
              '',
            );
          }
        })
        .catch((error) => {
          setOtp('');
          setErrorMsg(
            error?.response?.data?.message || 'Invalid code. Please try again.',
          );
        })
        .finally(() => {
          setIsLoading({ loader: false, type: 'verify' });
        });
    }
  };

  const onSkip = () => {
    if (!canSkip) return;
    localStorage.removeItem('mfaTimerStart'); // Clear timer after expiration
    handleLoginNavigation(loginResponse, history, customerObj, '');
  };

  return (
    <div>
      {step === 1 && (
        <div className="fade-in ">
          <div className="d-flex align-items-center mb-3">
            <img
              src={MFAAuthenticationLogo2}
              alt="MFA Authentication"
              className="me-3 mr-2"
              width="40"
              height="40"
            />
            <h2>Enable Multi-Factor Authentication</h2>
          </div>
          <div className="mb-3">
            <p>
              Add an extra layer of security to your account by using an
              authenticator app. You will be asked to scan a QR code.
            </p>
            <div className="alert alert-light border">
              <strong>Requirements:</strong>
              <ul className="mb-0">
                <li>Google Authenticator or Microsoft Auth</li>
                <li>Takes about 1 minute to complete</li>
              </ul>
            </div>
          </div>

          <div className="d-flex flex-column gap-3 mb-1">
            <Button
              onClick={handleContinue}
              className="btn-primary w-100 mb-3 "
            >
              {isLoading.loader && isLoading.type === 'continue' ? (
                <PageLoader color="#fff" type="button" />
              ) : (
                'Continue'
              )}
            </Button>

            <Button
              onClick={onSkip}
              className=" btn-transparent me-5 MFA-btn-border"
              disabled={
                (isLoading.loader && isLoading.type === 'continue') || !canSkip
              }
            >
              {canSkip
                ? 'Skip for now'
                : `Skip disabled for ${formatTime(remainingSeconds)}`}
            </Button>
          </div>
          {!canSkip ? (
            <div>
              <p
                className="text-muted small mb-2"
                style={{ color: '#FF5933', fontSize: '14px' }}
              >
                Secure your account now with MFA for instant access. Choosing to
                skip will result in a 1-minute security hold before you can
                proceed. [{formatTime(remainingSeconds)}]
              </p>
            </div>
          ) : null}
        </div>
      )}

      {step === 2 && (
        <div className="fade-in ">
          <div className="d-flex align-items-center mb-3">
            <img
              src={MFAAuthenticationLogo2}
              alt="MFA Authentication"
              className="me-3 mr-2"
              width="40"
              height="40"
            />
            <h2 className="mb-4">Scan this QR code</h2>
          </div>

          <div className="text-start d-inline-block mb-4 ">
            <ul
              style={{ listStyle: 'number', paddingLeft: '10px' }}
              className="mb-2"
            >
              <li>Open your authenticator app</li>
              <li>
                Tap <strong>add account</strong> or <strong>+</strong>
              </li>
              <li>Scan the QR code shown</li>
            </ul>

            <p className="mb-1 fw-bold">
              <strong>Supported apps:</strong>
            </p>
            <ul className="mb-0">
              <li>Google Authenticator</li>
              <li>Microsoft Authenticator</li>
            </ul>
          </div>

          <div className="d-flex justify-content-center mb-4">
            <div className="border p-2 rounded bg-white">
              <img
                src={qrData}
                alt="QR Code"
                style={{
                  width: '160px',
                  height: '160px',
                  objectFit: 'contain',
                }}
              />
            </div>
          </div>

          <p className="text-muted small mb-4">
            This QR code will only be shown once.
          </p>

          <div className="d-flex flex-column gap-3 mb-4">
            <Button onClick={handleNext} className="btn-primary w-100 mb-3">
              Proceed
            </Button>
            <Button
              onClick={onSkip}
              className="btn-transparent w-100 MFA-btn-border"
              disabled={!canSkip}
            >
              {canSkip
                ? 'Cancel'
                : `Cancel disabled for ${formatTime(remainingSeconds)}`}
            </Button>
          </div>
        </div>
      )}

      {/* --- Section 3: Verification --- */}
      {step === 3 && (
        <div className="fade-in">
          <div className="d-flex align-items-center mb-3">
            <img
              src={MFAAuthenticationLogo2}
              alt="MFA Authentication"
              className="me-3 mr-2"
              width="40"
              height="40"
            />
            <h2>Verify your code</h2>
          </div>
          <p className="mb-4">
            Enter the 6-digit code from your authenticator app to finalize
            setup.
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault(); // Prevent default form submission behavior
              handleVerify(); // Call the verify function
            }}
          >
            <div className="d-flex justify-content-center mb-3" width="80%">
              <OTPInput
                value={otp}
                onChange={handleOtpChange}
                numInputs={6}
                inputType="tel"
                renderSeparator={null}
                shouldAutoFocus
                renderInput={(props) => (
                  <input
                    {...props}
                    className="form-control text-center fw-bold fs-4 focus-ring focus-ring-primary border-secondary-subtle"
                    style={{
                      width: '48px',
                      height: '56px',
                      margin: '0 6px',
                      boxShadow: 'none',
                    }}
                  />
                )}
              />
            </div>
            {errorMsg && (
              <ErrorMsg className="text-center text-danger mb-3">
                {errorMsg}
              </ErrorMsg>
            )}
            <div className="d-flex flex-column gap-3 mb-4 mt-2">
              <Button
                type="submit"
                disabled={otp.length < 6}
                className="btn-primary mb-3 w-100"
              >
                {isLoading.loader && isLoading.type === 'verify' ? (
                  <PageLoader color={Theme.white} type="button" />
                ) : (
                  'Verify & Finish'
                )}
              </Button>

              {!isAlreadyScanned && (
                <Button
                  onClick={handleBack}
                  className="btn-transparent  MFA-btn-border"
                  disabled={isLoading.loader && isLoading.type === 'verify'}
                >
                  Back
                </Button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

MFAFlow.defaultProps = {
  isAlreadyScanned: false,
  userData: {
    email: '',
    password: '',
  },
  loginResponse: null,
};

MFAFlow.propTypes = {
  isAlreadyScanned: bool,
  userData: shape({
    email: string,
    password: string,
  }),
  loginResponse: shape({
    data: shape({
      email: string,
      customer: string,
    }),
  }),
};
